import { Tooltip } from '@zoom/components';
import { addMilliseconds, formatDistanceStrict } from 'date-fns';
import React, { memo } from 'react';
import {
  DragDropContext,
  DragStart,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  DropResult,
  Droppable,
  DroppableProvided,
} from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../app/AuthorizationProvider';
import { formatScheduleElementRGBAColor, getContrastColor, getTextShadowOfScheduleElement } from '../../../utils/chart';
import { ScheduleElement } from '../types';
import { DraggableItem } from './DraggableItem';

import '../../../styles/scheduling.scss';

export interface DragDropProps {
  defaultLengthMessage: (length: string) => string;
  onDragEnd: (elementId: string, commit: boolean) => void;
  onDragStart: (elementId: string) => void;
  scheduleElements: ScheduleElement[];
  isDragDisabled: boolean;
}

export const DragAndDropComponent: React.FC<DragDropProps> = ({
  defaultLengthMessage,
  isDragDisabled,
  onDragEnd,
  onDragStart,
  scheduleElements,
}) => {
  const { localeObj } = useAuth();

  const { t } = useTranslation();
  const onDragEndHandler = (result: DropResult) => {
    onDragEnd(result.draggableId, true);
  };

  const onDragStartHandler = (start: DragStart) => {
    onDragStart(start.draggableId);
  };

  return (
    <DragDropContext onDragEnd={onDragEndHandler} onDragStart={onDragStartHandler}>
      <Droppable droppableId="droppable" direction="horizontal" isDropDisabled>
        {(provided: DroppableProvided) => (
          <div
            ref={provided.innerRef}
            className="list"
            data-test-id="scheduling-composer:drag-and-drop-schedule-elements"
          >
            {scheduleElements.map((item: ScheduleElement, index: number) => (
              <Draggable key={item.id} draggableId={String(item.id)} index={index} isDragDisabled={isDragDisabled}>
                {(providedDraggable: DraggableProvided, snapshotDraggable: DraggableStateSnapshot) => {
                  const itemColor = formatScheduleElementRGBAColor(item.color);
                  const contrastColor = getContrastColor(item.color);

                  const helperDate = new Date();
                  const defaultDuration = formatDistanceStrict(
                    addMilliseconds(helperDate, item.defaultDuration),
                    helperDate,
                    { locale: localeObj }
                  );

                  const durationMessage = defaultLengthMessage(defaultDuration);
                  const tooltipMessage = item.pinnable ? (
                    <>
                      {durationMessage}
                      <br />
                      {t('schedule.activityCanBePinned')}
                    </>
                  ) : (
                    durationMessage
                  );
                  return (
                    <Tooltip testId="tooltip-content" message={tooltipMessage} placement="top">
                      <DraggableItem
                        providedDraggable={providedDraggable}
                        snapshotDraggable={snapshotDraggable}
                        item={item}
                        index={index}
                        itemStyle={{
                          background: itemColor,
                          color: formatScheduleElementRGBAColor(contrastColor),
                          textShadow: getTextShadowOfScheduleElement(contrastColor),
                        }}
                        parentStyle={{
                          background: itemColor,
                          ...providedDraggable.draggableProps.style,
                        }}
                        draggedElementStyle={{
                          width: item.defaultDropElementWidth || undefined,
                        }}
                      />
                    </Tooltip>
                  );
                }}
              </Draggable>
            ))}
            <div className="d-none">{provided.placeholder}</div>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export const DragAndDrop = memo(DragAndDropComponent);
